import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/utils/cn';

const ROUTE_LABELS: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/content':   'Content Management',
  '/users':     'Users',
  '/audit':     'Audit Log',
  '/settings':  'Settings',
};

export default function Topbar() {
  const { cmsUser, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const pageLabel =
    Object.entries(ROUTE_LABELS).find(([path]) =>
      location.pathname.startsWith(path)
    )?.[1] ?? 'WombCat CMS';

  return (
    <header className="fixed top-0 right-0 left-64 z-30 flex h-16 items-center justify-between border-b border-surface-200 bg-white px-6">

      {/* Page title */}
      <h1 className="text-base font-semibold text-surface-900">{pageLabel}</h1>

      {/* User menu */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setMenuOpen(o => !o)}
          className="flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm hover:bg-surface-100 transition-colors"
        >
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-semibold text-xs">
            {cmsUser?.displayName?.charAt(0).toUpperCase() ?? 'U'}
          </div>
          <div className="text-left hidden sm:block">
            <p className="text-sm font-medium text-surface-900 leading-none">
              {cmsUser?.displayName}
            </p>
            <p className="text-xs text-surface-400 leading-none mt-0.5 capitalize">
              {cmsUser?.role}
            </p>
          </div>
          <ChevronDown className={cn('h-4 w-4 text-surface-400 transition-transform', menuOpen && 'rotate-180')} />
        </button>

        {/* Dropdown */}
        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 w-52 rounded-xl border border-surface-200 bg-white shadow-lg py-1 animate-fade-in">
            <div className="px-4 py-2.5 border-b border-surface-100">
              <p className="text-sm font-medium text-surface-900 truncate">{cmsUser?.displayName}</p>
              <p className="text-xs text-surface-400 truncate">{cmsUser?.email}</p>
            </div>

            <button
              onClick={() => { setMenuOpen(false); logout(); }}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
